import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from 'Services/APIService';
import { Dispatch } from 'redux';
import { Member, Role, Department } from '../../../types/ndisSection/createStaffMembersTypes';

interface CreateStaffMembersState {
  searchKey: string;
  members: Member[];
  selectedMembers: string[];
  roles: { [email: string]: Role };
  departments: { [email: string]: Department };
  loading: boolean;
  error: string | null;
}

const initialState: CreateStaffMembersState = {
  searchKey: '',
  members: [],
  selectedMembers: [],
  roles: {},
  departments: {},
  loading: false,
  error: null
};

const createStaffMembersSlice = createSlice({
  name: 'createStaffMembers',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    hasError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setSearchKey(state, action: PayloadAction<string>) {
      state.searchKey = action.payload;
    },
    setMembers(state, action: PayloadAction<Member[]>) {
      state.members = action.payload;
      state.roles = {};
      state.departments = {};
    },
    setSelectedMembers(state, action: PayloadAction<string[]>) {
      state.selectedMembers = action.payload;
    },
    setRole(state, action: PayloadAction<{ email: string; role: Role; department?: Department }>) {
      state.roles[action.payload.email] = action.payload.role;
      if (action.payload.department) {
        state.departments[action.payload.email] = action.payload.department;
      }
    },
    resetForm(state) {
      state.searchKey = '';
      state.members = [];
      state.selectedMembers = [];
      state.roles = {};
      state.departments = {};
      state.error = null;
    },
    addStaffSuccess(state) {
      state.selectedMembers = [];
      state.roles = {};
      state.departments = {};
      state.error = null;
    }
  }
});

export default createStaffMembersSlice.reducer;

export const {
  setLoading,
  hasError,
  setSearchKey,
  setMembers,
  setSelectedMembers,
  setRole,
  resetForm,
  addStaffSuccess
} = createStaffMembersSlice.actions;

const handleError = (dispatch: Dispatch, error: any) => {
  const errorMessage =
    error?.response?.data?.error ||
    (error?.response?.status === 500 ? 'Server error, please try again later' : error?.message) ||
    'An error occurred';
  dispatch(hasError(errorMessage));
  console.error('API Error:', error);
};

export const fetchMembers =
  (payload: { group_id: string; service_name_id: string; search_key: string }) =>
  async (dispatch: Dispatch) => {
    dispatch(setLoading(true));
    try {
      if (payload.search_key.trim() === '') {
        dispatch(setMembers([]));
        return;
      }
      const response = await axiosInstance.post<{ membership_applicants: Member[] }>(
        '/ApprovedMembers/list',
        payload
      );
      dispatch(setMembers(response.data.membership_applicants || []));
    } catch (error) {
      handleError(dispatch, error);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const addStaff =
  (
    staffList: {
      id: string;
      email: string;
      username: string;
      ndis_role: Role;
      department?: Department;
      client_name: string;
    }[]
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(setLoading(true));
    try {
      const selectedClient = JSON.parse(localStorage.getItem('selectedClient') || '{}') as {
        email?: string;
      };
      const client_email = selectedClient.email || '';

      const payloadWithClientEmail = staffList.map((staff) => ({
        ...staff,
        client_email
      }));

      const response = await axiosInstance.post('/Ndis_Staff/add', payloadWithClientEmail);

      if (response.status === 200) {
        dispatch(addStaffSuccess());
      } else {
        dispatch(hasError('Failed to add staff.'));
      }
    } catch (error) {
      handleError(dispatch, error);
    } finally {
      dispatch(setLoading(false));
    }
  };
